// next-app/src/hooks/query-keys.ts

/**
 * Query key factory for TanStack Query.
 *
 * Hierarchical structure enables targeted invalidation:
 * - queryClient.invalidateQueries({ queryKey: queryKeys.events.all })
 *   invalidates both events list and individual events
 * - queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(id) })
 *   invalidates only that specific event
 *
 * @see https://tanstack.com/query/latest/docs/react/guides/query-keys
 */
export const queryKeys = {
  // Season
  season: {
    all: ['season'] as const,
    current: () => [...queryKeys.season.all, 'current'] as const,
  },

  // Authentication & Account
  auth: {
    all: ['auth'] as const,
    account: () => [...queryKeys.auth.all, 'account'] as const,
  },

  // Dealer
  dealer: {
    all: ['dealer'] as const,
    current: () => [...queryKeys.dealer.all, 'current'] as const,
  },

  // Events
  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters?: { seasonId?: number }) => [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.events.details(), String(id)] as const,
    eligibility: () => [...queryKeys.events.all, 'eligibility'] as const,
  },

  // Respondents (per event)
  respondents: {
    all: ['respondents'] as const,
    byEvent: (eventId: string | number) => [...queryKeys.respondents.all, String(eventId)] as const,
  },

  // 2FA
  twoFA: {
    all: ['2fa'] as const,
  },

  // Survey
  survey: {
    all: ['survey'] as const,
    config: (hash: string) => [...queryKeys.survey.all, 'config', hash] as const,
  },

  // Check-in/out
  check: {
    all: ['check'] as const,
    inData: (eventId: number) => [...queryKeys.check.all, 'in', eventId] as const,
    outData: (eventId: number) => [...queryKeys.check.all, 'out', eventId] as const,
  },

  // Stats
  stats: {
    all: ['stats'] as const,
    event: (eventId: number) => [...queryKeys.stats.all, 'event', eventId] as const,
  },

  // Training
  training: {
    all: ['training'] as const,
    progress: (eventId: number) => [...queryKeys.training.all, 'progress', eventId] as const,
  },
} as const;

/**
 * Type helper to extract query key types
 */
export type QueryKeyFactory = typeof queryKeys;
